import { ImportJobDocument, type ImportJobQuery, type ImportJobQueryVariables } from '@paintscout/api'
import { useApolloClient } from '@apollo/react-hooks'
import * as Sentry from '@sentry/react'
import { NetworkStatus } from 'apollo-client'
import { useEffect, useState } from 'react'

/**
 * How long one poll may take before it counts as failed. Without it, one request that never settles
 * would stop the loop, because the next poll only starts after the last one ends.
 */
export const IMPORT_JOB_POLL_TIMEOUT_MS = 10_000

export interface JobPollUpdate {
  networkStatus: NetworkStatus
  data?: ImportJobQuery
}

export interface JobPollerOptions {
  fetchJob: () => Promise<ImportJobQuery | undefined>
  intervalMs: number
  timeoutMs: number
  onUpdate: (update: JobPollUpdate) => void
  /** A failed or timed-out fetch. The loop carries on; this is for logging only. */
  onError?: (error: unknown) => void
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(`importJob poll timed out after ${ms}ms`)), ms)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Fetches the job at once, then again `intervalMs` after each fetch ends, until `stop()`. A failed
 * or timed-out fetch is reported and the loop carries on. Nothing is reported after `stop()`, so a
 * late response for an old job id is dropped.
 */
export function createJobPoller({ fetchJob, intervalMs, timeoutMs, onUpdate, onError }: JobPollerOptions): {
  stop: () => void
} {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | undefined

  async function tick() {
    if (stopped) {
      return
    }
    try {
      onUpdate({ networkStatus: NetworkStatus.poll })
      try {
        const data = await withTimeout(fetchJob(), timeoutMs)
        if (!stopped) {
          onUpdate({ networkStatus: NetworkStatus.ready, data })
        }
      } catch (err) {
        if (!stopped) {
          onError?.(err)
          onUpdate({ networkStatus: NetworkStatus.error })
        }
      }
    } catch {
      // A throwing callback must not end the loop: a loop that stops is the bug this replaces (#25226).
    } finally {
      if (!stopped) {
        timer = setTimeout(tick, intervalMs)
      }
    }
  }

  void tick()

  return {
    stop: () => {
      stopped = true
      clearTimeout(timer)
    }
  }
}

export interface ImportJobPollingOptions {
  id?: string
  skip: boolean
  /** 0 pauses polling and keeps the last job. */
  pollInterval: number
}

export interface ImportJobPollingResult {
  data?: ImportJobQuery
  networkStatus?: NetworkStatus
}

/**
 * Polls `importJob` with our own loop instead of `useQuery`'s `skip` + `pollInterval` (#25226).
 * On `@apollo/react-hooks` 3.1.3 that pair stopped polling one request after the job id arrived, so
 * the dialog never saw the job finish. While skipped, it also kept polling with `id: undefined`.
 * Returns the same `{ data, networkStatus }` shape as the query hook.
 */
export function useImportJobPolling({ id, skip, pollInterval }: ImportJobPollingOptions): ImportJobPollingResult {
  const client = useApolloClient()
  const [result, setResult] = useState<ImportJobPollingResult & { id?: string }>({})
  const active = !skip && !!id

  useEffect(() => {
    if (!active || !id || pollInterval <= 0) {
      return
    }
    const jobId = id

    const poller = createJobPoller({
      fetchJob: async () => {
        const res = await client.query<ImportJobQuery, ImportJobQueryVariables>({
          query: ImportJobDocument,
          variables: { id: jobId },
          fetchPolicy: 'network-only'
        })
        return res.data
      },
      intervalMs: pollInterval,
      timeoutMs: IMPORT_JOB_POLL_TIMEOUT_MS,
      // A breadcrumb, not an event: the dialog already tells the user, and a flaky network is normal.
      onError: (err) =>
        Sentry.addBreadcrumb({
          category: 'import',
          level: 'warning',
          message: 'importJob poll failed',
          data: { importJobId: jobId, error: err instanceof Error ? err.message : String(err) }
        }),
      // A failed poll keeps the last job, as Apollo does, so progress stays where it was.
      onUpdate: ({ networkStatus, data }) =>
        setResult((prev) => ({ id: jobId, networkStatus, data: data ?? (prev.id === jobId ? prev.data : undefined) }))
    })

    return poller.stop
  }, [client, id, active, pollInterval])

  if (!active || result.id !== id) {
    return { data: undefined, networkStatus: undefined }
  }
  return { data: result.data, networkStatus: result.networkStatus }
}
